/**
 * @file interpret/components/CallModeControls.tsx
 * @brief Call mode for the Interpreter (desktop app only): interpret a Zoom,
 *        Teams or Meet call.
 *
 * @details
 * docs/product/desktop-call-interpretation-plan.md. On the Interpreter's setup,
 * under Microphone:
 * - **Call mode** switch: the other side comes from the call's audio and its
 *   translation plays in your headphones.
 * - **Send to the call**: the device the call app uses as its microphone (the
 *   Verblizr virtual mic, picked automatically when it is installed). Your
 *   translation plays into it. "Nowhere" keeps every translation on this Mac.
 *   Verblizr Microphone is also made the Mac's microphone while Call mode is
 *   on (useCallMicDefault.ts), so the call app needs no setting.
 * - **Hear my translation too**: on by default (DES 03-10-2026): your own
 *   translation also plays in your headphones as it goes into the call.
 * - **They hear their translation too**: on by default (DES 03-10-2026,
 *   "how would he know whether it was right"): theirs also goes into the call.
 * - **Only send the translation**: off by default (DES 02-10-2026, option 2):
 *   your voice goes into the call live, and the translation follows.
 *
 * The settings stay on this computer (localStorage), like the desktop app's
 * other settings.
 *
 * @author DES
 * @date 02-10-2026
 */

import { useEffect, useMemo, useState } from "react";
import { ChevronsUpDown, Headphones, LoaderCircle, Mic, Speaker } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { FormFieldGroup } from "@/components/ui/FormSheet";
import { PickerSheet, type PickerSheetItem } from "@/components/ui/PickerSheet";
import { Switch } from "@/components/ui/Switch";
import { desktopShell, type CallMicStatus } from "@/shared/desktop/desktopShell";
import { toast } from "@/shared/hooks/useToast";
import type { CallSettings } from "./callSettings";
import { connectedHeadphones, hearOptions, isCallSendDevice } from "./callMicDevices";
import { useOutputDevices } from "./useOutputDevices";

/**
 * Driver 2.0.0 splits the device: we play into "Verblizr Output" and the
 * call app hears "Verblizr Microphone". The user only ever thinks of the
 * microphone, so that is the name shown.
 */
function callMicName(label: string): string {
  return /verblizr/i.test(label) ? "Verblizr Microphone" : label;
}

/** "Zoom", "Zoom and Teams", "Zoom, Teams and Chrome". */
function joinNames(names: string[]): string {
  if (names.length <= 1) return names[0] ?? "";
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

type DeviceItem = PickerSheetItem & { deviceId: string };

const NOWHERE = "__nowhere__";
const AUTO_HEAR = "__auto__";

export function CallModeControls({
  value,
  onChange,
  callApps = null,
}: {
  value: CallSettings;
  onChange: (next: CallSettings) => void;
  /** The call apps going on, named in the switch's line. */
  callApps?: string[] | null;
}) {
  const [reloadKey, setReloadKey] = useState(0);
  const outputs = useOutputDevices(value.on, reloadKey);
  const platform = desktopShell()?.platform;
  const windows = platform === "win32";
  const here = windows ? "this PC" : "this Mac";
  // Only Verblizr Microphone carries a translation into a call (DES
  // 03-10-2026): not the Mac's speakers, not Teams' own share device.
  // On Windows, VB-Cable stands in for it while we test (06-10-2026).
  const devices = useMemo(
    () => outputs.filter((d) => isCallSendDevice(d.label, platform)),
    [outputs, platform],
  );
  const [pickerOpen, setPickerOpen] = useState(false);
  const [hearOpen, setHearOpen] = useState(false);

  // Where you hear their translation (DES 03-10-2026: with headphones on it
  // still came from the Mac, whose own output was the speakers).
  const headphones = connectedHeadphones(outputs);
  const hearChoices = hearOptions(outputs);
  const hearChosen = hearChoices.find((d) => d.deviceId === value.hearDeviceId) ?? null;
  const hearingOn = hearChosen?.label ?? headphones?.label ?? `${windows ? "This PC" : "This Mac"}'s sound output`;
  const hearItems = useMemo<DeviceItem[]>(
    () => [
      {
        id: AUTO_HEAR,
        deviceId: "",
        label: "Headphones when connected",
        subtitle: headphones ? `Now: ${headphones.label}` : `Now: ${here}'s sound output`,
      },
      ...hearChoices.map((d) => ({ id: d.deviceId, deviceId: d.deviceId, label: d.label })),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [outputs],
  );

  // The virtual mic ships inside the desktop app (DESKTOP/src/callMic.js):
  // offer to install it here, once, if this Mac has not got it (or has an
  // older copy than this app carries).
  const shell = desktopShell();
  const [micStatus, setMicStatus] = useState<CallMicStatus | null>(null);
  const [installing, setInstalling] = useState(false);
  useEffect(() => {
    if (!value.on || !shell?.callMicStatus) return;
    let cancelled = false;
    void shell
      .callMicStatus()
      .then((s) => {
        if (!cancelled) setMicStatus(s);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [value.on, shell, reloadKey]);
  const ourDeviceListed = devices.some((d) => /verblizr/i.test(d.label));
  const offerInstall =
    !!shell?.installCallMic &&
    !!micStatus?.available &&
    (!micStatus.installed || !micStatus.upToDate || !ourDeviceListed);

  const installMic = async () => {
    if (!shell?.installCallMic) return;
    setInstalling(true);
    try {
      const res = await shell.installCallMic();
      if (res.ok) {
        toast({ variant: "success", title: "Verblizr Microphone is ready" });
        // Pick it up: the list refreshes and the effect below selects it.
        onChange({ ...value, sendDeviceId: "", sendLabel: "" });
        setReloadKey((k) => k + 1);
      } else if (res.error !== "CANCELLED") {
        toast({
          variant: "destructive",
          title: "Could not add Verblizr Microphone",
          description: res.error,
        });
      }
    } finally {
      setInstalling(false);
    }
  };

  // The Verblizr virtual mic, once installed, is chosen for you, unless
  // "Nowhere" was picked on purpose.
  useEffect(() => {
    if (!value.on || value.sendDeviceId === NOWHERE) return;
    // Kept unless it is gone (or an older pick: speakers, Teams' device).
    if (devices.some((d) => d.deviceId === value.sendDeviceId)) return;
    const ours = devices[0];
    if (ours) onChange({ ...value, sendDeviceId: ours.deviceId, sendLabel: ours.label });
  }, [devices, value, onChange]);

  // Settings saved before the name was kept: fill it in.
  useEffect(() => {
    const picked = devices.find((d) => d.deviceId === value.sendDeviceId);
    if (picked && picked.label !== value.sendLabel) onChange({ ...value, sendLabel: picked.label });
  }, [devices, value, onChange]);

  const items = useMemo<DeviceItem[]>(
    () => [
      {
        id: NOWHERE,
        deviceId: NOWHERE,
        label: "Nowhere",
        subtitle: `Every translation plays on ${here}`,
      },
      ...devices.map((d) => ({
        id: d.deviceId,
        deviceId: d.deviceId,
        label: callMicName(d.label),
      })),
    ],
    [devices, here],
  );
  const chosen = devices.find((d) => d.deviceId === value.sendDeviceId) ?? null;
  // A device chosen before and now gone (unplugged, uninstalled) is "Nowhere".
  const sendingTo = chosen ? callMicName(chosen.label) : "Nowhere";

  return (
    <div className="space-y-3">
      <label className="flex items-center justify-between gap-3 border-b border-app-text/10 pb-2.5">
        <span className="min-w-0">
          <span className="block text-[15px] font-medium">Call mode</span>
          <span className="block text-xs text-muted-foreground">
            {callApps && callApps.length > 0
              ? `${joinNames(callApps)} on ${here}: interpret the call.`
              : "Zoom, Teams or Meet: the other side is translated into your headphones."}
          </span>
        </span>
        <Switch
          checked={value.on}
          onCheckedChange={(on) => onChange({ ...value, on })}
          aria-label="Call mode"
        />
      </label>

      {value.on ? (
        <>
          <FormFieldGroup
            label="Hear their translation on"
            hint={
              hearChosen || headphones
                ? "Their translation plays here, not on the Mac's speakers."
                : "Connect headphones; they are picked by themselves."
            }
          >
            <PickerSheet<DeviceItem>
              anchor={
                <button
                  type="button"
                  className="flex min-h-6 w-full items-center gap-2 rounded-md text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <Headphones className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden />
                  <span className="min-w-0 flex-1 truncate text-[17px] font-medium leading-6">
                    {hearingOn}
                  </span>
                  <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" aria-hidden />
                </button>
              }
              open={hearOpen}
              onOpenChange={setHearOpen}
              title="Hear their translation on"
              subtitle="Your headphones, so the call does not hear it twice."
              searchPlaceholder="Search devices…"
              items={hearItems}
              selectedId={hearChosen ? hearChosen.deviceId : AUTO_HEAR}
              onSelect={(item) => {
                onChange({ ...value, hearDeviceId: item.deviceId });
                setHearOpen(false);
              }}
              clearable={false}
            />
          </FormFieldGroup>

          <FormFieldGroup
            label="Send your translation to the call"
            hint={
              chosen && /verblizr/i.test(chosen.label)
                ? "Your Mac's microphone while a session runs, so the call uses it."
                : chosen && windows
                  ? "In Zoom, Teams or Meet, choose CABLE Output as your microphone."
                  : chosen
                    ? "Choose this as your microphone in Zoom, Teams or Meet."
                    : offerInstall
                      ? "Add Verblizr Microphone below to speak into the call."
                      : windows
                        ? "Install VB-Cable to speak into the call; until then every translation plays on this PC."
                        : "Every translation plays on this Mac."
            }
          >
            <PickerSheet<DeviceItem>
              anchor={
                <button
                  type="button"
                  className="flex min-h-6 w-full items-center gap-2 rounded-md text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <Speaker className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden />
                  <span className="min-w-0 flex-1 truncate text-[17px] font-medium leading-6">
                    {sendingTo}
                  </span>
                  <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" aria-hidden />
                </button>
              }
              open={pickerOpen}
              onOpenChange={setPickerOpen}
              title="Send to the call"
              subtitle="Verblizr Microphone carries your translation into the call."
              searchPlaceholder="Search devices…"
              items={items}
              selectedId={chosen ? chosen.deviceId : NOWHERE}
              onSelect={(item) => {
                onChange({
                  ...value,
                  sendDeviceId: item.deviceId,
                  sendLabel: item.deviceId === NOWHERE ? "" : item.label,
                });
                setPickerOpen(false);
              }}
              clearable={false}
            />
          </FormFieldGroup>

          {offerInstall ? (
            <div className="space-y-1.5">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={installing}
                onClick={() => void installMic()}
                className="gap-1.5"
              >
                {installing ? (
                  <LoaderCircle className="h-4 w-4 animate-spin" aria-hidden />
                ) : (
                  <Mic className="h-4 w-4" aria-hidden />
                )}
                {micStatus?.installed ? "Update Verblizr Microphone" : "Add Verblizr Microphone"}
              </Button>
              <p className="text-xs text-muted-foreground">
                Once on this Mac. It asks for your Mac password, and sound pauses for a second.
              </p>
            </div>
          ) : null}

          <label
            className={
              "flex items-center justify-between gap-3 border-b border-app-text/10 pb-2.5" +
              (chosen ? "" : " opacity-50")
            }
          >
            <span className="min-w-0">
              <span className="block text-[15px] font-medium">Only send the translation</span>
              <span className="block text-xs text-muted-foreground">
                Off: your voice goes live, then the translation follows.
              </span>
            </span>
            <Switch
              checked={value.translationOnly}
              disabled={!chosen}
              onCheckedChange={(translationOnly) => onChange({ ...value, translationOnly })}
              aria-label="Only send the translation"
            />
          </label>

          <label
            className={
              "flex items-center justify-between gap-3 border-b border-app-text/10 pb-2.5" +
              (chosen ? "" : " opacity-50")
            }
          >
            <span className="min-w-0">
              <span className="block text-[15px] font-medium">Hear my translation too</span>
              <span className="block text-xs text-muted-foreground">
                What the call hears from you, also in your headphones, to check it.
              </span>
            </span>
            <Switch
              checked={value.hearOwn}
              disabled={!chosen}
              onCheckedChange={(hearOwn) => onChange({ ...value, hearOwn })}
              aria-label="Hear my translation too"
            />
          </label>

          <label
            className={
              "flex items-center justify-between gap-3 border-b border-app-text/10 pb-2.5" +
              (chosen ? "" : " opacity-50")
            }
          >
            <span className="min-w-0">
              <span className="block text-[15px] font-medium">They hear their translation too</span>
              <span className="block text-xs text-muted-foreground">
                Their translation also goes into the call, so they can check it.
              </span>
            </span>
            <Switch
              checked={value.theyHearOwn}
              disabled={!chosen}
              onCheckedChange={(theyHearOwn) => onChange({ ...value, theyHearOwn })}
              aria-label="They hear their translation too"
            />
          </label>

          <label
            className={
              "flex items-center justify-between gap-3 border-b border-app-text/10 pb-2.5" +
              (chosen ? "" : " opacity-50")
            }
          >
            <span className="min-w-0">
              <span className="block text-[15px] font-medium">Beep for their turn</span>
              <span className="block text-xs text-muted-foreground">
                A soft beep in the call after your translation, so they know to speak.
              </span>
            </span>
            <Switch
              checked={value.turnBeep}
              disabled={!chosen}
              onCheckedChange={(turnBeep) => onChange({ ...value, turnBeep })}
              aria-label="Beep for their turn"
            />
          </label>

          <p className="flex items-start gap-1.5 text-[13px] leading-[18px] text-muted-foreground opacity-70">
            <Headphones className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
            Works on the Mac's speakers too. With headphones you can also talk over a translation.
          </p>
        </>
      ) : null}
    </div>
  );
}
